import { Button, Tooltip } from '@poseidon/components/Input2';
import { cn } from '@poseidon/components/cn';
import { General } from '@poseidon/components/StrokeIcons';

import type { SequenceStepTone } from '~/components/Sequence/sequenceStepStatus';
import { NOW_CHIP_CLASS } from '~/components/Sequence/sequenceStepStatus';

interface Props {
  /**
   * The live tone of the node this marker sits beside (blue running / orange needs-you / amber blocked /
   * red errored), so the flag and the node read as one unit. Defaults to the plain running blue.
   */
  tone?: SequenceStepTone;
  /**
   * The stage the contact is actually resting on, e.g. "Waiting for working hours", "Hit LinkedIn limit".
   * Answers "why isn't this moving?" without opening anything. Omit → just the flag.
   */
  stageLabel?: string | null;
  /**
   * When the contact is parked on this step's call/reminder to-do: complete it and advance the sequence
   * past the manual step. Omit → the marker alone (nothing for a rep to do here).
   */
  onMarkDone?: () => void;
}

/**
 * The left-gutter row on the LOCKED per-contact graph, beside the node the contact is on right now: a
 * "NOW" flag (tinted to match the node's state), the stage they're sitting on, plus whatever action is
 * available there. The builder fills the same gutter with the team's people-piles — a per-contact graph has
 * no pile to draw and no face worth drawing: the whole page is already about one person, so an avatar here
 * would only repeat the header. Marker and control share ONE flex row so they can never overlap.
 */
export function SequenceCurrentStepMarker({ tone = 'running', stageLabel, onMarkDone }: Props) {
  return (
    <div className="flex items-center gap-1.5">
      {onMarkDone != null && (
        <Button
          size="2xs"
          color="green"
          tone="soft"
          icon={<General.CheckCircle />}
          tooltip="Mark done — complete this to-do and advance the sequence"
          onClick={onMarkDone}
          className="whitespace-nowrap">
          Mark done
        </Button>
      )}
      <Tooltip tooltip="Where this contact is right now.">
        <span
          className={cn(
            'flex cursor-default items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset',
            NOW_CHIP_CLASS[tone],
          )}>
          Now
          {stageLabel != null && <span className="font-medium normal-case opacity-80">· {stageLabel}</span>}
        </span>
      </Tooltip>
    </div>
  );
}
